import { Reveal } from "./Reveal";
import { IMAGES } from "../../data/destinations";
import { useLenis } from "lenis/react";
import { ArrowRight } from "lucide-react";

const CHAPTERS = [
  {
    n: "01",
    title: "Follow Your Curiosity",
    subtitle: "It starts with a conversation.",
    paras: [
      "There will always be another reason to wait. We work with travelers who are ready to go, to experience somewhere new, follow their curiosity, and see where it leads.",
      "We begin by getting to know you: how you like to travel, what you're drawn to, your pace, priorities, and what you want this particular journey to feel like. You don't need to have it all figured out. That's where we come in.",
    ],
    image: IMAGES.coastalTunnel,
    testid: "manifesto-chapter-1",
  },
  {
    n: "02",
    title: "We Bring It to Life",
    subtitle: "We turn possibility into a thoughtfully designed trip.",
    paras: [
      "The best trips don't just take you somewhere new. They move you forward. Drawing on our expertise, firsthand knowledge, and trusted relationships around the world, we create a journey that's distinctly yours.",
      "From hotels and private guides to memorable meals, local experiences, and the details in between, we thoughtfully connect the pieces. Together, we refine the itinerary until it feels right.",
    ],
    image: "https://images.pexels.com/photos/36836853/pexels-photo-36836853/free-photo-of-colorful-alley-in-chefchaouen-morocco.jpeg?auto=compress&cs=tinysrgb&w=1400",
    testid: "manifesto-chapter-2",
  },
  {
    n: "03",
    title: "Let the Magic Unfold",
    subtitle: "You go. We take care of the rest.",
    paras: [
      "Life moves like a wheel, and the Ruota circle is intentionally left open because every journey is still unfolding.",
      "Before you depart, we make sure every detail is in place. While you're away, you'll have trusted support when you need it, giving you the freedom to be present, explore, and leave a little room for the unexpected.",
    ],
    image: IMAGES.tuscany,
    testid: "manifesto-chapter-3",
  },
];

export const Manifesto = () => {
  const lenis = useLenis();

  return (
  <section id="philosophy" data-testid="manifesto-section" className="bg-[#F7F0E3] py-24 sm:py-36">
    <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
      <Reveal>
        <button
          data-testid="manifesto-planning-cta"
          onClick={() => lenis?.scrollTo("#enquiry", { offset: -60, duration: 1.6 })}
          className="group mb-10 flex items-center gap-3 rounded-full bg-[#168A91] px-7 py-3.5 font-accent text-[11px] uppercase tracking-[0.2em] text-[#F7F0E3] transition-colors duration-300 hover:bg-[#E2725B] hover:text-[#0D1F23]"
        >
          Let's Get Planning
          <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
        </button>
        <p className="font-display text-3xl font-medium uppercase tracking-[0.15em] text-[#E2725B] sm:text-4xl">
          Our Approach
        </p>
        <h2 className="mt-5 max-w-2xl font-display text-3xl font-light leading-tight tracking-tight text-[#0D1F23] sm:text-5xl">
          From inspiration to <span className="italic text-[#168A91]">departure</span>.
        </h2>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-[#4A5557] sm:text-lg">
          Every trip begins with an idea. A place you've been dreaming about, an experience you
          want to share, or simply the feeling that it's time to go somewhere new.
        </p>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-[#4A5557] sm:text-lg">
          We start by understanding what moves you, then bring together our expertise, trusted
          relationships, and thoughtful planning to create an experience that feels distinctly
          yours.
        </p>
      </Reveal>

      <div className="mt-20 flex flex-col gap-24 sm:mt-28 sm:gap-32">
        {CHAPTERS.map((c, i) => (
          <div
            key={c.n}
            data-testid={c.testid}
            className="grid items-center gap-10 md:grid-cols-12 md:gap-14"
          >
            <div className={`md:col-span-7 ${i % 2 === 1 ? "md:order-2" : ""}`}>
              <Reveal>
                <span className="font-display text-7xl font-light leading-none text-[#E2725B] sm:text-8xl">
                  {c.n}
                </span>
                <h3 className="mt-4 font-display text-2xl font-light uppercase tracking-wide text-[#0D1F23] sm:text-3xl">
                  {c.title}
                </h3>
                <p className="mt-3 font-display text-xl italic text-[#168A91]">{c.subtitle}</p>
                {c.paras.map((p) => (
                  <p key={p.slice(0, 24)} className="mt-4 max-w-lg text-base leading-relaxed text-[#4A5557]">
                    {p}
                  </p>
                ))}
              </Reveal>
            </div>
            <div className={`md:col-span-5 ${i % 2 === 1 ? "md:order-1" : ""}`}>
              <Reveal delay={0.15}>
                <div
                  className={`overflow-hidden ${
                    i % 2 === 0 ? "rounded-tr-[4rem] rounded-bl-[4rem]" : "rounded-tl-[4rem] rounded-br-[4rem]"
                  }`}
                >
                  <img
                    src={c.image}
                    alt={c.title}
                    loading="lazy"
                    className="h-72 w-full object-cover transition-transform duration-[1.4s] ease-out hover:scale-105 sm:h-80"
                  />
                </div>
              </Reveal>
            </div>
          </div>
        ))}
      </div>

      <Reveal>
        <div data-testid="planning-design" className="mx-auto mt-24 max-w-3xl border-t-2 border-[#E2725B] pt-12">
          <h3 className="font-display text-2xl font-light uppercase tracking-[0.15em] text-[#168A91] sm:text-3xl">
            Planning & Design
          </h3>
          <p className="mt-6 text-base leading-relaxed text-[#4A5557] sm:text-lg">
            Every trip we design is highly personalized and requires thoughtful research,
            planning, and collaboration with our trusted partners around the world.
          </p>
          <p className="mt-4 text-base leading-relaxed text-[#4A5557] sm:text-lg">
            Our professional planning fees reflect the time and expertise that go into creating
            a seamless, one-of-a-kind travel experience. Fees vary based on the length and
            complexity of your trip and will always be shared with you before we begin.
          </p>
          <div className="mt-8 rounded-2xl bg-white p-7 shadow-sm">
            <p className="font-display text-xl italic text-[#13272B]">Just need a hotel?</p>
            <p className="mt-3 text-base leading-relaxed text-[#4A5557]">
              We can take care of that, too. Through our relationships with exceptional hotels
              around the world, we can help you find and book the right stay, often with
              preferred rates, amenities, and added benefits. Hotel-only bookings typically do
              not require a planning fee.
            </p>
          </div>
          <button
            data-testid="planning-cta-button"
            onClick={() => lenis?.scrollTo("#enquiry", { offset: -60, duration: 1.6 })}
            className="group mt-9 flex items-center gap-3 rounded-full bg-[#168A91] px-7 py-3.5 font-accent text-[11px] uppercase tracking-[0.2em] text-[#F7F0E3] transition-colors duration-300 hover:bg-[#116D73]"
          >
            Where Will You Go Next?
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>
      </Reveal>

      <Reveal>
        <p className="mt-24 text-center font-display text-3xl font-light italic tracking-tight text-[#0D1F23] sm:text-4xl">
          Your Journey in <span className="text-[#E2725B]">Motion</span>.
        </p>
      </Reveal>
    </div>
  </section>
  );
};
